'use client';

import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { Button, Text } from '@instamenu/ui-system-next';
import { Dropdown, DropdownContainer } from '@instamenu/ui-system-next';
import { Gift, Star } from 'lucide-react';
import { numItems as numItemsStyle } from '../cart-menu/cart-menu.css';
import { useMediaQuery } from 'react-responsive';
import { themeVars, radii, breakpoints } from '@instamenu/ui-vanilla-extract';
import { loyaltyPtsText } from './navbar.css';
import { useLoyaltyInit } from '../loyalty-status/use-loyalty-init';
import { RewardModal } from '../reward-modal/reward-modal';
import { LoyaltyCard3D } from '../loyalty-status/loyalty-card-3d';
import tinycolor from 'tinycolor2';
import { headline, arrow, subtext, ctaButton } from '../loyalty-welcome/loyalty-welcome-modal.css';
import { loyaltyBadge } from '../menu-item/menu-item.css';
import {
  loyaltyBadgeButton,
  desktopDropdownCard,
  mobileOverlay,
  mobileCard,
} from './loyalty-nav-badge.css';

function LoyaltyDropdownContent({
  accentColor,
  brandDark,
  programName,
  pointsBalance,
  pointsPerDollar,
  hasReward,
  rewardsAvailable,
  rewardGroupNames,
  onRedeem,
  isItemBased,
  earningThreshold,
  progress,
  justEnrolled,
}: {
  accentColor?: string;
  brandDark: string;
  programName: string;
  pointsBalance: number;
  pointsPerDollar: number;
  hasReward: boolean;
  rewardsAvailable: number;
  rewardGroupNames: string[];
  onRedeem: () => void;
  isItemBased: boolean;
  earningThreshold: number;
  progress: number;
  justEnrolled: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px 20px' }}>
      <LoyaltyCard3D
        programName={programName}
        rewardsAvailable={rewardsAvailable}
        pointsBalance={pointsBalance}
        accentColor={accentColor}
        isItemBased={isItemBased}
        earningThreshold={earningThreshold}
        progress={progress}
        onClick={hasReward ? onRedeem : undefined}
      />

      {hasReward ? (
        <>
          <div className={headline} style={{ fontSize: 16 }}>
            <span className={arrow}>→</span>
            {isItemBased
              ? `${rewardsAvailable} free ${rewardsAvailable !== 1 ? 'items' : 'item'} to redeem!`
              : `You have ${pointsBalance.toLocaleString()} points!`}
          </div>
          <Text variant="caption1" css={{ color: '#6B6868', fontSize: 13 }}>
            {isItemBased
              ? progress > 0
                ? `${progress}/${earningThreshold} toward your next free item`
                : 'Redeem your free item!'
              : 'Redeem your points for free menu items.'}
          </Text>
          <Button
            className={ctaButton}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onRedeem();
            }}
            style={{ marginTop: 4 }}
          >
            Redeem now
          </Button>
        </>
      ) : (
        <>
          <div className={headline} style={{ fontSize: 16 }}>
            <span className={arrow}>→</span>
            {justEnrolled ? 'Welcome!' : 'Welcome back!'}
          </div>
          <Text variant="caption1" css={{ color: '#6B6868', fontSize: 13 }}>
            {isItemBased ? (
              <>
                You have {progress}/{earningThreshold} punches.{' '}
                {earningThreshold - progress > 0
                  ? `${earningThreshold - progress} more to earn a free item!`
                  : 'Almost there!'}
              </>
            ) : (
              <>
                You have {pointsBalance.toLocaleString()} points! Earn {pointsPerDollar} pts per
                dollar. Keep ordering!
              </>
            )}
          </Text>
          <div className={subtext} style={{ fontSize: 12, marginTop: 4 }}>
            Items marked with{' '}
            <span
              className={loyaltyBadge}
              style={{ verticalAlign: 'middle', position: 'relative', top: -1 }}
            >
              <Star size={10} fill="#fff" stroke="#fff" />
            </span>{' '}
            qualify for the reward — buy those to earn {isItemBased ? 'punches' : 'points'}.
          </div>
        </>
      )}
    </div>
  );
}

export const LoyaltyNavBadge = observer(() => {
  const { checkout, loyalty } = useLoyaltyInit();
  const [visible, setVisible] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const isDesktop = useMediaQuery({ query: breakpoints.tablet });

  // Auto-open dropdown after enrollment
  useEffect(() => {
    if (loyalty?.justEnrolled) {
      setVisible(true);
    }
  }, [loyalty?.justEnrolled]);

  // Dismiss the mobile overlay (and the desktop dropdown) with the Escape
  // key. Without this, keyboard users have no way to close the panel once
  // it's open — the only existing dismissal path is a click on the
  // backdrop.
  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible]);

  const brandColor = checkout?.order.restaurant.restaurantGroup?.color ?? undefined;
  const brandDark = useMemo(() => tinycolor(brandColor).darken(15).toString(), [brandColor]);

  if (!loyalty?.isActive || !loyalty.initialized || !loyalty.status) return null;

  const program = loyalty.program;
  const hasReward = loyalty.rewardsAvailable > 0;
  const programName = program?.displayName ?? 'Rewards';
  const rewardGroupNames = checkout
    ? loyalty.rewardGroupNames(checkout.menu.itemGroups.map((ig) => ({ id: ig.id, name: ig.name })))
    : [];

  const dropdownProps = {
    accentColor: brandColor,
    brandDark,
    programName,
    pointsBalance: loyalty.pointsBalance,
    pointsPerDollar: loyalty.pointsPerDollar,
    hasReward,
    rewardsAvailable: loyalty.rewardsAvailable,
    rewardGroupNames,
    onRedeem: () => {
      setVisible(false);
      setRedeemOpen(true);
    },
    isItemBased: loyalty.isItemBased,
    earningThreshold: loyalty.earningThreshold,
    progress: loyalty.progress,
    justEnrolled: !!loyalty.justEnrolled,
  };

  return (
    <>
      <DropdownContainer
        onDismiss={() => {
          setVisible(false);
          if (loyalty?.justEnrolled) {
            runInAction(() => {
              loyalty.justEnrolled = false;
            });
          }
        }}
      >
        <div
          onClick={() => setVisible(!visible)}
          className={loyaltyBadgeButton}
          style={{
            height: themeVars.sizes.inputHeight.sm,
            borderRadius: radii.button,
            border: themeVars.border.default,
          }}
        >
          <Gift size={20} color={themeVars.color.brand} style={{ display: 'block' }} />
          {loyalty.isItemBased ? (
            loyalty.rewardsAvailable > 0 ? (
              <Text variant="caption2" className={numItemsStyle}>
                {loyalty.rewardsAvailable}
              </Text>
            ) : (
              <span className={loyaltyPtsText}>
                {loyalty.progress}/{loyalty.earningThreshold}
              </span>
            )
          ) : (
            <span className={loyaltyPtsText}>{loyalty.pointsBalance.toLocaleString()} pts</span>
          )}
        </div>

        <Dropdown visible={isDesktop && visible} align="right">
          <div className={desktopDropdownCard}>
            <LoyaltyDropdownContent {...dropdownProps} />
          </div>
        </Dropdown>

        {!isDesktop && visible && (
          <div
            className={mobileOverlay}
            onClick={() => setVisible(false)}
            role="dialog"
            aria-modal="true"
            aria-label={programName}
          >
            <div className={mobileCard} onClick={(e) => e.stopPropagation()}>
              <LoyaltyDropdownContent {...dropdownProps} />
            </div>
          </div>
        )}
      </DropdownContainer>

      <RewardModal
        open={redeemOpen}
        onClose={() => setRedeemOpen(false)}
        onRewardApplied={() => setRedeemOpen(false)}
        rewardItemGroupIds={program?.rewardItemGroupIds ?? []}
        pointsBalance={loyalty.pointsBalance}
        isItemBased={loyalty.isItemBased}
        earningThreshold={loyalty.earningThreshold}
        rewardsAvailable={loyalty.rewardsAvailable}
        progress={loyalty.progress}
        rewardIncludesModifiers={loyalty.rewardIncludesModifiers}
      />
    </>
  );
});
